import { storageGet, storageSet } from "./storage";

export const INTAKE_COOLDOWN_KEY = "liq_intake_retry_at";
export const INTAKE_COOLDOWN_EVENT = "listino:intake-cooldown";
const MAX_WAIT_SECONDS = 7 * 24 * 60 * 60;
const PDF_ERRORS = [
  "pdf_invalid",
  "pdf_encrypted",
  "pdf_empty",
  "pdf_too_large",
  "pdf_too_many_pages",
  "pdf_validation_timeout",
  "pdf_validation_limit",
  "pdf_validation_unavailable",
];
let fallbackRetryAt = 0;

/** @typedef {{code: string, error: string, status: number, retry_at?: number}} IntakeFailure */

/** @param {unknown} value */
const positiveSeconds = (value) =>
  typeof value === "number" && Number.isSafeInteger(value) && value > 0 && value <= MAX_WAIT_SECONDS
    ? value
    : null;

/** @param {any} headers @param {number} now */
const headerSeconds = (headers, now) => {
  let fromGetter;
  try {
    fromGetter = typeof headers?.get === "function" ? headers.get("retry-after") : null;
  } catch {
    // A malformed header adapter must not hide the original safe rejection.
  }
  const raw = fromGetter ?? headers?.["retry-after"] ?? headers?.["Retry-After"];
  if (typeof raw === "number") return positiveSeconds(raw);
  if (typeof raw !== "string" || raw.length > 128) return null;
  const value = raw.trim();
  if (/^\d+$/.test(value)) return positiveSeconds(Number(value));
  if (!/^[A-Za-z]{3},/.test(value)) return null;
  const time = Date.parse(value);
  return Number.isFinite(time) ? positiveSeconds(Math.ceil((time - now) / 1000)) : null;
};

/** Pure transport parser. Unknown/malformed wait hints never become a timer.
 * @param {any} failure @param {number} [now] @returns {IntakeFailure | null}
 */
export const parseIntakeFailure = (failure, now = Date.now()) => {
  const status = failure?.response?.status;
  const data = failure?.response?.data;
  if (status === 429) {
    const seconds =
      positiveSeconds(data?.retry_after_seconds) ?? headerSeconds(failure.response.headers, now);
    return {
      code: "intake_limit_exceeded",
      error: "Too many listing or upload attempts. Please wait before trying again.",
      status,
      ...(seconds === null ? {} : { retry_at: now + seconds * 1000 }),
    };
  }
  if (
    status === 503 &&
    ["intake_budget_unavailable", "pdf_validation_unavailable"].includes(data?.code)
  ) {
    return {
      code:
        data?.code === "pdf_validation_unavailable"
          ? "pdf_validation_unavailable"
          : "intake_budget_unavailable",
      error:
        data?.code === "pdf_validation_unavailable"
          ? "We couldn't check this PDF right now. Please try again."
          : "We couldn't accept this request right now. Please try again.",
      status,
    };
  }
  return null;
};

/** A partial import can report a quota pause in an otherwise successful response.
 * Ordinary active-import polling delays are not intake cooldowns.
 * @param {any} progress @param {number} [now] @returns {IntakeFailure | null}
 */
export const parseImportIntakeFailure = (progress, now = Date.now()) => {
  if (progress?.status !== "partial") return null;
  if (progress.error_code === "intake_limit_exceeded") {
    return parseIntakeFailure(
      {
        response: {
          status: 429,
          data: {
            retry_after_seconds: progress.retry_after_seconds,
          },
        },
      },
      now
    );
  }
  if (progress.error_code === "intake_budget_unavailable") {
    return parseIntakeFailure(
      {
        response: {
          status: 503,
          data: {
            code: progress.error_code,
          },
        },
      },
      now
    );
  }
  return null;
};

/** @param {number} [now] */
export const currentIntakeRetryAt = (now = Date.now()) => {
  const stored = Number(storageGet(INTAKE_COOLDOWN_KEY));
  const valid = (/** @type {number} */ value) =>
    Number.isSafeInteger(value) && value > now && value <= now + MAX_WAIT_SECONDS * 1000
      ? value
      : 0;
  return Math.max(valid(stored), valid(fallbackRetryAt));
};

/** Persist only a timestamp, never addresses, capabilities, or selected files.
 * @param {number | undefined} retryAt
 */
export const rememberIntakeCooldown = (retryAt) => {
  const now = Date.now();
  if (
    !Number.isSafeInteger(retryAt) ||
    !retryAt ||
    retryAt <= now ||
    retryAt > now + MAX_WAIT_SECONDS * 1000
  )
    return;
  const next = Math.max(currentIntakeRetryAt(now), retryAt);
  storageSet(INTAKE_COOLDOWN_KEY, String(next));
  fallbackRetryAt = storageGet(INTAKE_COOLDOWN_KEY) === String(next) ? 0 : next;
  window.dispatchEvent(new Event(INTAKE_COOLDOWN_EVENT));
};

/** @param {any} failure */
export const captureIntakeFailure = (failure) => {
  const parsed = parseIntakeFailure(failure);
  if (parsed) rememberIntakeCooldown(parsed.retry_at);
  return parsed;
};

/** @param {any} error @param {number | null | undefined} [status] */
export const retryableIntakeFailure = (error, status) =>
  (status ?? error?.status) === 429 ||
  ["intake_limit_exceeded", "intake_budget_unavailable", "pdf_validation_unavailable"].includes(
    error?.code
  );

/** Safe copy for the known intake/PDF envelopes; null preserves ordinary field validation.
 * @param {any} error @param {number} [retryAt]
 */
export const intakeErrorText = (error, retryAt = currentIntakeRetryAt()) => {
  const now = Date.now();
  const safeExpiry = (/** @type {any} */ value) =>
    Number.isSafeInteger(value) && value > 0 && value <= now + MAX_WAIT_SECONDS * 1000 ? value : 0;
  const until = Math.max(safeExpiry(error?.retry_at), safeExpiry(retryAt));
  if (error?.code === "intake_limit_exceeded" || until > now) {
    const seconds = Math.max(0, Math.ceil((until - now) / 1000));
    if (seconds > 0) {
      const [divisor, unit] =
        seconds >= 86400
          ? [86400, "day"]
          : seconds >= 3600
            ? [3600, "hour"]
            : seconds >= 60
              ? [60, "minute"]
              : [1, "second"];
      const count = Math.ceil(seconds / Number(divisor));
      const wait = `${count} ${unit}${count === 1 ? "" : "s"}`;
      return `Too many listing or upload attempts. Please wait ${wait}, then try again.`;
    }
    return until
      ? "You can try again now. Your request will not restart automatically."
      : "Too many listing or upload attempts. Please wait a moment, then try again.";
  }
  if (["intake_budget_unavailable", "pdf_validation_unavailable"].includes(error?.code)) {
    return typeof error.error === "string"
      ? error.error
      : "We couldn't accept this request right now. Please try again.";
  }
  if (
    typeof error?.code === "string" &&
    PDF_ERRORS.includes(error.code) &&
    typeof error.error === "string"
  )
    return error.error;
  return null;
};
